import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import useClampedLeft from './useClampedLeft';

interface Props {
  top: number;
  left: number;
  actionLabel: string;
  onAccept: () => void;
  onReject: () => void;
}

const RephrasePreviewControls: FC<Props> = ({
  top,
  left,
  actionLabel,
  onAccept,
  onReject,
}) => {
  const { ref, clampedLeft } = useClampedLeft(left);
  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);

  // Keyboard support: focus lands on the primary action when the popover
  // appears, and Escape rejects the suggestion (standard escape route).
  useEffect(() => {
    acceptButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onReject();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onReject]);

  return createPortal(
    <div
      ref={ref}
      className="medium-editor-toolbar medium-editor-toolbar-active medium-toolbar-arrow-over"
      style={{ top, left: clampedLeft, transform: 'translate(-50%, 0)' }}
      role="toolbar"
      aria-label="AI suggestion actions"
      data-test-id="ai-rephrase-controls"
    >
      <div className="ai-rephrase-controls">
        <span className="ai-rephrase-controls-label">{actionLabel}</span>
        <button
          ref={acceptButtonRef}
          type="button"
          className="ai-rephrase-accept"
          onClick={onAccept}
          data-test-id="ai-rephrase-accept"
        >
          Accept
        </button>
        <button
          type="button"
          className="ai-rephrase-reject"
          onClick={onReject}
          data-test-id="ai-rephrase-reject"
        >
          Reject
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default RephrasePreviewControls;
